/**
 * OpentokRecording DeviceManager implementation.
 *
 * @copyright Realeyes OU. All rights reserved.
 */

import MediaStream from './MediaStream';
import VideoTrack from './VideoTrack';
import AudioTrack from './AudioTrack';
import Device from './Device';
import fixMediaInfoAtSafari131 from '../utils/safariHelper';

/**
 * @implements {IDeviceManager}
 */
export default class DeviceManager {
    /**
     * Instantiates a DeviceManager
     *
     * @param {MediaStreamConstraints} constraints Constraints for getUserMedia.
     */
    constructor(constraints) {
        this.gumConstraints = constraints;
        this.activeDevices = [];
    }

    /**
     * @return {Promise}
     */
    init() {
        return this.getDevices();
    }

    /**
     * @param {Array.<MediaDevices>} devices The list of devices which should be used to get media stream.
     * @return {Promise}
     */
    getStream(devices = []) {
        return this.getInitPermissionAndUpdateDevices().then(() => {
        let constraints = { ...this.gumConstraints };
        if (devices.length) {
            const audio = devices.find(d => d.deviceType === 'audio');
            const video = devices.find(d => d.deviceType === 'video');

            if (video) {
                constraints.video.deviceId = video.id ? { exact: video.id } : undefined;
            } else {
                constraints.video = false;
            }

            if (audio) {
                constraints.audio = { deviceId: audio.id ? { exact: audio.id } : undefined };
            } else {
                constraints.audio = false;
            }

            if (!constraints.video && !constraints.audio) {
                constraints = this.gumConstraints;
            }
        } 

        return navigator.mediaDevices.getUserMedia(constraints)
            .then((gumStream) => {
                const streamTracks = gumStream.getTracks();

                streamTracks.forEach((track) => {
                    const activeDevice = this.findActiveDevice(track);
                    const deleteIndex = this.activeDevices.findIndex(device => device.deviceType === track.kind);
                    if (deleteIndex !== -1) {
                        this.activeDevices.splice(deleteIndex, 1);
                    }
                    this.activeDevices.push(activeDevice);
                });

                return Promise.all(streamTracks.map(t => this.createTrack(gumStream, t)))
                    .then(tracks => new MediaStream(gumStream, tracks));
            });
        })
    }

    /**
     * Finds the active device for a GUM Track
     * @param {IMediaStreamTrack} track GUM track
     * @return {Device}
     */
    findActiveDevice(track) {
        let d = this.devices.find(device => device.deviceType === track.kind && device.name === track.label);

        /* These hacks for browsers (like Sogou Explorer). These browsers are webRTC capable browsers,
        * but they have some issues with standard compabilities. For example they can't read the webRTC
        * device's name just the id of device. This is why we need this hack, and find the next capable device
        * without the equality of device.name and track.label */
        /* istanbul ignore if */
        if (!d) {
            const activeDevice = this.activeDevices.find(device => device.deviceType === track.kind);

            const activeDeviceIndex = !activeDevice ? -1 : this.devices.findIndex(
                device => device.deviceType === track.kind
                                                            && device.id === activeDevice.id,
            );

            d = this.devices.find((device, index) => device.deviceType === track.kind && index > activeDeviceIndex);

            // If current active device was the last capable device, then the next active device must be the first
            // capable device
            if (!d) {
                d = this.devices.find(device => (device.deviceType === track.kind));
            }

            d.name = track.label;
        }

        return d;
    }

    /**
     * @param {MediaStream} stream GUM stream
     * @return {Promise}
     */
    createVideoTrack(stream) {
        return new Promise((resolve) => {
            const video = document.createElement('video');
            video.id = 'validator-output';
            video.style.width = '100px';
            video.muted = true;
            video.srcObject = stream;
            video.playsinline = '';
            video.setAttribute('playsinline', ''); // safari-mobile-compatibility
            video.setAttribute('muted', ''); // safari-mobile-compatibility

            const loadedMetadataHandler = () => {
                const activeDevice = this.activeDevices.find(device => device.deviceType === 'video');
                const videoDevice = new Device(activeDevice.id, activeDevice.name, activeDevice.deviceType);
                // IOS14 Safari do not let play video immediately
                setTimeout(() => {
                    video.play();
                }, 1000);

                resolve(new VideoTrack(video, video.videoWidth, video.videoHeight, videoDevice));
            };

            video.addEventListener('loadedmetadata', loadedMetadataHandler);
        });
    }

    /**
     * @param {MediaStream} stream GUM stream
     * @return {Promise}
     */
    createAudioTrack(stream) {
        const activeDevice = this.activeDevices.find(device => device.deviceType === 'audio');
        const audioDevice = new Device(activeDevice.id, activeDevice.name, activeDevice.deviceType);

        return Promise.resolve(new AudioTrack(stream, audioDevice));
    }

    /**
     * @param {MediaStream} stream GUM stream
     * @param {IMediaStreamTrack} track GUM track
     * @return {Promise}
     */
    createTrack(stream, track) {
        if (track.kind === 'audio') return this.createAudioTrack(stream);
        return this.createVideoTrack(stream);
    }

    getInitPermissionAndUpdateDevices()
    {
        return new Promise((resolve, reject) => {
            navigator.mediaDevices.getUserMedia(this.gumConstraints).then(
                () => {
                    resolve(this.getDevices());
                }, reject
            );            
        });
    }

    /**
     * @return {Promise.<Array.<IDevice>>}
     */
    getDevices() {
        return new Promise((resolve, reject) => {
            navigator.mediaDevices.enumerateDevices().then(
                (info) => {
                    let camCount = 0;
                    let micCount = 0;

                    const fixedInfo = fixMediaInfoAtSafari131(info);

                    this.devices = fixedInfo.filter(d => d.kind === 'audioinput' || d.kind === 'videoinput').map(d => ({
                        id: d.deviceId,
                        name: d.label || (d.kind === 'audioinput' ? `mic ${micCount++}` : `cam ${camCount++}`),
                        deviceType: d.kind === 'audioinput' ? 'audio' : 'video',
                    }));

                    resolve(this.devices);
                }, reject,
            );
        });
    }
}
